import { Box, Button, CircularProgress, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import Breadcrumbs from 'components/breadcrumbs';
import Collapsible from 'components/collapsible';
import DatePicker from 'components/date-picker';
import Divider from 'components/divider';
import FilterSidebar from 'components/filter-sidebar';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import LoadingSpinner from 'components/loading-spinner';
import Paper from 'components/paper';

import ASCSmallCheck from 'components/asc/asc-small-check';

import TextField from 'components/text-field';
import { useAccountState } from 'contexts/account/account-context';
import dayjs, { Dayjs } from 'dayjs';
import { useGet } from 'hooks/use-get';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { useJsonToCsv } from 'react-json-csv';
import { useHistory, useParams } from 'react-router';
import _ from 'underscore';
import googleTagManager from 'utils/google-tag-manager';
import slugify from 'utils/slugify';
import { SiteAutocomplete } from 'views/order-feed/components/site-autocomplete';
import { OrderFeedProvider } from 'views/order-feed/context/order-feed-context';
import { OrderFeedSettingsProvider } from 'views/order-feed/context/order-feed-settings-context';
import { useSitesState } from 'views/order-feed/context/sites-context';
import { useGoogleTagManager } from 'views/order-feed/features/my-supply-plan/hooks/use-google-tag-manager';
import { SupplyPlanType } from 'views/order-feed/features/my-supply-plan/types/google-tag-manager/order-feed-events.type';
import { getDetailedOrderHistory } from 'views/order-feed/features/order-history/api/get-order-history';
import { GetHistoricalOrdersInput } from 'views/order-feed/features/order-history/types/get-historical-orders-input.type';
import { OrderFeedParams } from 'views/order-feed/types/order-feed-params.type';
import { Site } from 'views/order-feed/types/site.type';
import OrderFeedTabsHeader from '../../components/order-feed-tabs-header';
import OrderHistoryRowBody from './components/order-history-row-body';

type Props = {};

const today = dayjs();
const PAGE_NAME_ID = 'orderFeed.orderHistory';

const OrderHistory: React.FC<Props> = () => {
  const { account } = useAccountState();
  const intl = useIntl();
  const params = useParams<OrderFeedParams>();
  const history = useHistory();
  const { saveAsCsv } = useJsonToCsv();
  const { createOrderFeedEvent } = useGoogleTagManager();

  const accountSites = account.company?.addressAccess.filter((s) => !!s.siteErpId);
  const {
    sitesQuery: { data: sites },
  } = useSitesState();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const OrderHistoryHeaders: HeadCell[] = useMemo(() => {
    return [
      {
        id: 'orderNumber',
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.ordersList.orderNumberLabel` }),
        align: 'left',
      },
      {
        id: 'dispatchDate',
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.ordersList.dispatchDate` }),
        align: 'left',
      },
      {
        id: 'deliveryMethod',
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.ordersList.deliveryMethod` }),
        align: 'left',
      },
      {
        id: 'orderedQuantity',
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.ordersList.orderedQuantity` }),
        align: 'right',
      },
      {
        id: 'invoicedQuantity',
        label: intl.formatMessage({ id: `${PAGE_NAME_ID}.ordersList.invoicedQuantity` }),
        align: 'right',
      },
      { id: 'actions', label: '' },
    ];
  }, [intl]);

  // build map of urls based on address_1 (only commonality between orders data from OrderHistory endpoint and sites)
  const urlMap: { [key: string]: string } = useMemo(
    () =>
      [...(sites || [])].reduce(
        (acc, curr) => ({
          ...acc,
          [curr.name]: `${curr.siteErpId}/${curr.globalSiteId}`,
          [curr.erpName]: `${curr.siteErpId}/${curr.globalSiteId}`,
          [curr.siteErpId]: `${curr.siteErpId}/${curr.globalSiteId}`,
        }),
        {}
      ),
    [sites]
  );

  const [values, setValues] = useState<Site[]>([]);

  const [searchValue, setSearchValue] = useState<string>('');
  const [fromDate, setFromDate] = useState<Dayjs>(today.subtract(3, 'months'));
  const [toDate, setToDate] = useState<Dayjs>(today);

  useEffect(() => {
    if (params.globalSiteId) {
      setValues(sites?.filter((site) => site.globalSiteId === params.globalSiteId) || []);
    }
  }, [params.globalSiteId, sites]);

  const detailedHistoryParams: GetHistoricalOrdersInput = useMemo(() => {
    return {
      startDate: fromDate.toISOString(),
      endDate: toDate.toISOString(),
      siteIds: _.uniq(values.map((site) => site.globalSiteId)),
    };
  }, [fromDate, toDate, values]);

  const { data, isLoading, isFetching, isError } = useGet(
    [
      'getOrderHistory',
      account.company!.companyId,
      detailedHistoryParams.startDate,
      detailedHistoryParams.endDate,
      detailedHistoryParams.siteIds,
    ],
    ({ queryKey }) => getDetailedOrderHistory(String(queryKey[1]), detailedHistoryParams),
    {
      placeholderData: (previousData) => previousData,
      enabled:
        (account.company?.features?.hasOrderRead || account.company?.features?.hasOrderFeed) &&
        !!account.company?.companyId &&
        !!values.length,
    }
  );

  const groups = useMemo(() => {
    if (!data) return {};
    return _.chain(data)
      .sortBy((o) => o.customerAddress.replace(/[0-9]/g, '')) // sort by alphabetical, trim numbers
      .groupBy((o) => o.customerAddress)
      .value();
  }, [data]);

  const filteredGroups = useMemo(() => {
    if (!searchValue) return groups;
    const term = searchValue.toLowerCase();
    const result: typeof groups = {};
    Object.keys(groups).forEach((siteAddress) => {
      const matches = groups[siteAddress].filter(
        (o) => o.orderNumber.toLowerCase().includes(term) || o.customersOrderNumber.toLowerCase().includes(term)
      );
      if (matches.length > 0) result[siteAddress] = matches;
    });
    return result;
  }, [groups, searchValue]);

  const exportToCSV = () => {
    let obArr: any[] = [];
    let title = slugify(intl.formatMessage({ id: `${PAGE_NAME_ID}.title` }));
    let filename = `${title}-${dayjs().format('L LT')}`;

    const siteOrders = _.chain(data)
      .groupBy((item) => item.customerAddress)
      .value();

    Object.keys(siteOrders).forEach((site) => {
      const rows = _.chain(siteOrders[site])
        .map((o) => ({
          ...o,
          dispatchDate: dayjs(o.dispatchDate).format('L'),
        }))
        .sortBy((line) => dayjs(line.dispatchDate))
        .reverse()
        .value();

      let emptyOrder = Object.keys(rows[0] || {}).reduce((acc, key) => ({ ...acc, [key]: '' }), {});
      let emptyRow = Object.keys(rows[0].orderLines || {}).reduce((acc, key) => ({ ...acc, [key]: '' }), { emptyOrder });

      obArr.push({ ...emptyRow, orderNumber: site });

      rows.forEach((order) => {
        obArr.push(order);
        let orderLines = order.orderLines.map((line) => ({ ...line, dispatchDate: dayjs(line.dispatchDate).format('L') }));
        orderLines.forEach((line) => {
          let o = Object.assign({}, order);
          obArr.push(Object.assign(o, line));
        });
        obArr.push(emptyRow);
      });
    });

    let fields = OrderHistoryHeaders.filter((col) => col.id !== 'actions').reduce(
      (acc, curr) => ({ ...acc, [curr.id]: curr.label }),
      {}
    );

    Object.assign(fields, {
      deliveredQuantity: intl.formatMessage({ id: `${PAGE_NAME_ID}.ordersList.deliveredQuantity` }),
      itemNumber: intl.formatMessage({ id: `${PAGE_NAME_ID}.ordersList.articleNumber` }),
      itemName: intl.formatMessage({ id: `${PAGE_NAME_ID}.ordersList.product` }),
      lineNumber: intl.formatMessage({ id: `${PAGE_NAME_ID}.ordersList.lineNumber` }),
    });

    saveAsCsv({
      data: obArr,
      fields,
      filename,
    });

    googleTagManager.push({
      event: 'download',
      fileName: filename,
      fileType: 'csv',
      siteId: params.globalSiteId,
      pageUrl: window.location.pathname,
    });
  };

  return (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <OrderFeedTabsHeader
        utils={
          <Breadcrumbs
            crumbs={
              params.siteErpId
                ? [
                    {
                      text:
                        account.company?.addressAccess.find((s) => s.siteErpId === params.siteErpId)?.name ||
                        params.siteErpId,
                      url: `${params.siteErpId}/${params.globalSiteId}`,
                    },
                  ]
                : undefined
            }
          />
        }
      />
      <Box display={'flex'} justifyContent={'flex-end'}>
        <SiteAutocomplete multiple values={values} onChange={setValues} route={{ basePath: '/order-feed/order-history' }} />
      </Box>
      <Paper transparent>
        <Box display={'flex'} flexDirection={'column'} gap={4}>
          <FilterSidebar
            clearFilters={() => {
              setFromDate(today.subtract(3, 'months'));
              setToDate(today);
              setSearchValue('');
            }}
            showClearFilter={!fromDate.isSame(today.subtract(3, 'months')) || !toDate.isSame(today)}>
            <DatePicker
              fullWidth={isMobile}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.fromDateLabel` })}
              disableFuture={true}
              value={fromDate}
              onChange={(date) => {
                setFromDate(date);
              }}
              slotProps={{
                shortcuts: {
                  sx: {
                    '& .MuiChip-root': (theme) => ({
                      backgroundColor: 'transparent',
                      color: theme.palette.darkBlue.main,
                      border: `1px solid ${theme.palette.darkBlue.main}`,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }),
                  },
                  items: [
                    {
                      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.pastMonth` }),
                      getValue: () => toDate.subtract(1, 'month'),
                    },
                    {
                      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.pastThreeMonth` }),
                      getValue: () => toDate.subtract(3, 'months'),
                    },
                    {
                      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.pastSixMonth` }),
                      getValue: () => toDate.subtract(6, 'months'),
                    },
                  ],
                },
              }}
            />

            <DatePicker
              fullWidth={isMobile}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.toDateLabel` })}
              disableFuture={true}
              value={toDate}
              onChange={(date) => {
                setToDate(date);
              }}
            />

            <TextField
              label={intl.formatMessage({ id: 'general.search' })}
              value={searchValue}
              endAdornment={<Search size={18} />}
              onChange={(event) => {
                setSearchValue(event.target.value);
              }}
            />

            <Button
              onClick={() => {
                exportToCSV();
              }}>
              <FormattedMessage id={`general.exportCSVBtn`} />
            </Button>
          </FilterSidebar>
          {!!values.length && !isError && !_.isEmpty(data) && (
            <Box display={'flex'} flexDirection={'column'} gap={4}>
              <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                <Typography color="white" variant="subtitle1">
                  {intl.formatMessage(
                    { id: `${PAGE_NAME_ID}.dateRange` },
                    {
                      fromDate: <FormattedDate value={fromDate.toISOString()} />,
                      toDate: <FormattedDate value={toDate.toISOString()} />,
                    }
                  )}
                </Typography>
                {isFetching && <CircularProgress sx={{ color: theme.palette.common.white }} size={16} />}
              </Box>
              <Divider />
            </Box>
          )}
          <Grid size={12}>
            {isLoading && !data ? (
              <Paper>
                <LoadingSpinner />
              </Paper>
            ) : !!values.length && !isError && _.isEmpty(data) ? (
              <Paper>
                <Box display={'flex'} justifyContent={'center'}>
                  <Typography color={theme.palette.darkBlue.main} variant="body2">
                    <FormattedMessage id="general.noResults" />
                  </Typography>
                </Box>
              </Paper>
            ) : (
              !!values.length &&
              Object.keys(filteredGroups).map((siteAddress, idx) => {
                const group = _.sortBy(filteredGroups[siteAddress], 'dispatchDate').reverse();
                const siteOption = sites?.find((s) => s.name === siteAddress || s.erpName === siteAddress);
                const site = accountSites?.find(
                  (accountSite) =>
                    accountSite.id === siteOption?.globalSiteId || accountSite.siteErpId === siteOption?.siteErpId
                );
                return (
                  <Collapsible
                    label={<Typography component="span">{siteAddress}</Typography>}
                    hideBorder
                    key={`collapsible_${siteAddress}`}
                    open={!!searchValue || idx === 0}
                    count={group.length}>
                    <List
                      dark
                      key={`${siteAddress}_${idx}`}
                      options={OrderHistoryHeaders}
                      noResult={{
                        show: _.isEmpty(data),
                        text: <FormattedMessage id={`${PAGE_NAME_ID}.ordersList.noResults`} />,
                      }}>
                      {group.map((order, idxx) => {
                        return (
                          <ListRow
                            key={`${order.orderNumber}_${idxx}`}
                            body={{
                              options: [],
                              span: 8,
                              children: <OrderHistoryRowBody order={order} site={site} />,
                            }}>
                            <ListCell>
                              <Box display={'flex'} alignItems={'center'} gap={5}>
                                {order.orderNumber}
                                {order.ascCompliant && <ASCSmallCheck dark />}
                              </Box>
                            </ListCell>
                            <ListCell>
                              <FormattedDate value={order.dispatchDate} />
                            </ListCell>
                            <ListCell>{order.deliveryMethod}</ListCell>
                            <ListCell align="right">
                              <FormattedNumber value={order.orderedQuantity} minimumFractionDigits={0} maximumFractionDigits={0} />
                            </ListCell>
                            <ListCell align="right">
                              <FormattedNumber value={order.invoicedQuantity} minimumFractionDigits={0} maximumFractionDigits={0} />
                            </ListCell>
                            <ListCell align="right">
                              <Button
                                size="small"
                                onClick={() => {
                                  createOrderFeedEvent('view', {
                                    orderNumber: order.orderNumber,
                                    supplyPlanType: SupplyPlanType.OFD1,
                                  });

                                  history.push(
                                    `/order-feed/order-history/${urlMap[order.customerAddress] || 'undefined/undefined'}/${order.orderNumber}`,
                                    {
                                      documentIds: order.documentIds,
                                      orderNumber: order.orderNumber,
                                      dispatchDate: order.dispatchDate,
                                      siteName: order.customerName,
                                    }
                                  );
                                }}>
                                <FormattedMessage id={`general.view`} />
                              </Button>
                            </ListCell>
                          </ListRow>
                        );
                      })}
                    </List>
                  </Collapsible>
                );
              })
            )}
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

const OrderHistoryContainer: React.FC<Props> = () => (
  <OrderFeedProvider>
    <OrderFeedSettingsProvider>
      <OrderHistory />
    </OrderFeedSettingsProvider>
  </OrderFeedProvider>
);

export default OrderHistoryContainer;
